import { AxiosResponse } from "axios";
import { IAnyModelType, isStateTreeNode } from "mobx-state-tree";
import { IEnumApi } from "mobx-stores/_models/enum-api";
import { numericFormatter } from "react-number-format";
import { OptionItem } from "./dynamic-form/models/control";
export * from "./option-item/option-item-ultil";

export const avatarColor = (string: string): string | undefined => {
  if (!string) return undefined;
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};
export const avatarString = (name: string): string => {
  if (!name) return "";
  const str =
    `${name.split(" ")?.[0]?.[0] || ""}${name.split(" ")?.[1]?.[0] || ""}` ||
    "";
  return str;
};
type NumberFormatAsStringOption = {
  emtyWhenZero?: boolean;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
};
export const numberFormatAsString = (
  number?: string | null,
  opt: NumberFormatAsStringOption = {
    emtyWhenZero: false,
    decimalScale: undefined,
    fixedDecimalScale: true,
  },
) => {
  if (!number) return "";
  if (opt.emtyWhenZero && (!number || number === "0")) return "";
  return numericFormatter(number || "", {
    thousandSeparator: true,
    decimalScale: opt.decimalScale,
    fixedDecimalScale: opt.fixedDecimalScale,
  });
};

export const getFileType = (fileName: string): string | null => {
  const extension = fileName.split(".").pop()?.toLowerCase(); // Get the file extension
  return extension || null; // Return the extension or null if no extension
};

export const isFileType = (
  fileName: string,
  allowedTypes: string[],
): boolean => {
  const fileType = getFileType(fileName);
  return fileType ? allowedTypes.includes(fileType) : false;
};

export const downloadFile = (content: Blob | string, fileName: string) => {
  const link = document.createElement("a");

  if (content instanceof Blob) {
    // If content is a Blob (e.g., binary data or file content)
    const url = URL.createObjectURL(content);
    link.href = url;
  } else {
    // If content is a plain string (e.g., base64 or text)
    const blob = new Blob([content], { type: "text/plain" }); // Adjust MIME type as needed
    const url = URL.createObjectURL(blob);
    link.href = url;
  }

  link.download = fileName; // Set the desired file name
  link.style.display = "none";

  document.body.appendChild(link); // Append link to the DOM
  link.click(); // Trigger the download
  document.body.removeChild(link); // Clean up the DOM

  if (content instanceof Blob) {
    URL.revokeObjectURL(link.href); // Free up memory for Blob URLs
  }
};

export const downloadFileFromBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  downloadFile(url, fileName);
  URL.revokeObjectURL(url);
};

export const downloadFileFromAxiosBlob = (axiosBlob: AxiosResponse<Blob>) => {
  const fileName = getFileNameFromContentDisposition(
    axiosBlob.headers["content-disposition"],
  );
  downloadFileFromBlob(axiosBlob.data, fileName);
};

export const getFileNameFromContentDisposition = (
  contentDisposition: string,
) => {
  const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const fileNameMatch = regex.exec(contentDisposition);
  if (fileNameMatch?.[1]) {
    return fileNameMatch[1].replaceAll(/['"]/g, "");
  }
  return "download";
};

export const populateEnumApiToOptionItem = (
  enumApi: IEnumApi[],
): OptionItem[] => {
  return enumApi.map((item) => ({
    value: item.key,
    label: item.value,
  }));
};

export const fileToBinaryString = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result) {
        const binaryString = Array.from(
          new Uint8Array(reader.result as ArrayBuffer),
        )
          .map((byte) => byte.toString(2).padStart(8, "0"))
          .join("");
        resolve(binaryString);
      } else {
        reject(new Error("Failed to read file."));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error reading file."));
    };

    reader.readAsArrayBuffer(file);
  });
};

export const roundTo = (num: number, decimalScale: number): number => {
  const factor = Math.pow(10, decimalScale);
  return Math.round((num + Number.EPSILON) * factor) / factor;
};

export const convertObjectToMobXModel = <T>(
  object: T,
  Model: IAnyModelType,
): T => {
  return isStateTreeNode(object) ? object : Model.create(object);
};

export const formatNumber = (
  value: number | null | undefined,
  options?: {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): string => {
  const numberValue = typeof value === "number" ? value : 0;

  const locale = options?.locale;
  const minimumFractionDigits = options?.minimumFractionDigits;
  const maximumFractionDigits = options?.maximumFractionDigits;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(numberValue);
};

export const createConditionalNaturalLanguageString = (
  condition: Map<
    string,
    { label: string; value: string | number | null | undefined }
  >,
): string => {
  let result = "";
  condition.forEach((value, key) => {
    if (value.value && value.value.toString().trim() !== "") {
      if (result !== "") {
        result += ", ";
      }
      result += `${value.label}: ${value.value}`;
    }
  });
  return result ? `Tìm theo: ${result}` : "";
};
